import React, { useEffect, useRef, useState } from 'react'
import ArrowForwardRoundedIcon from '@material-ui/icons/ArrowForwardRounded'
import styles from './Home.module.css'
import { getLandingImageProps, heroImageSizes } from '../../utils/landingImages'

const heroImages = [
  { src: '/images/ge.jpg', alt: 'GE appliances in a contemporary kitchen' },
  { src: '/images/whirlpool.jpg', alt: 'Whirlpool washer and dryer in a laundry room' },
  { src: '/images/frigidaire.jpg', alt: 'Frigidaire kitchen appliances' },
]
const brandLogos = [
    'air-king_1.png',
    'amana_1.png',
    'avanti_orig.png',
    'blomberg.png',
    'bosch_1.png',
    'broan.png',
    'electrolux.png',
    'fisher-paykel.png',
    'friedrich.png',
    'frigidaire.png',
    'gaggenau.png',
    'ge.png',
    'hotpoint.png',
    'kitchenaid.png',
    'lg.png',
    'marvel.png',
    'maytag.png',
    'monogram.png',
    'premier.png',
    'sharp.png',
    'speed-queen.png',
    'summit.png',
    'thermador.png',
    'viking-2.png',
    'whirlpool.png',
    'zephyr.png',
]

function ServiceDialog({ open, onClose, labelId, children }) {
  const dialogRef = useRef(null)
  const closeRef = useRef(null)
  useEffect(() => {
    if (!open) return undefined
    const trigger = document.activeElement
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    return () => {
      document.body.style.overflow = previousOverflow
      trigger?.focus()
    }
  }, [open])

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
    }
    if (event.key !== 'Tab') return
    const controls = dialogRef.current.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), [tabindex="0"]')
    const first = controls[0]
    const last = controls[controls.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last?.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first?.focus()
    }
  }

  return (
    <div className={styles.modalOverlay} hidden={!open} onClick={onClose}>
      <div ref={dialogRef} className={styles.modal} role="dialog" aria-modal="true" aria-labelledby={labelId} onKeyDown={handleKeyDown} onClick={(event) => event.stopPropagation()}>
        <button ref={closeRef} type="button" className={styles.modalClose} onClick={onClose} aria-label="Close">×</button>
        {children}
        <div className={styles.modalActions}>
          <button className={styles.secondaryCta} type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

const Home = () => {
  const yearsInBusiness = Math.max(0, new Date().getFullYear() - 1988)
  const [heroIndex, setHeroIndex] = useState(0)
  const [heroPaused, setHeroPaused] = useState(false)
  const [heroInteracting, setHeroInteracting] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)
  const [brandIndex, setBrandIndex] = useState(0)
  const [activeModal, setActiveModal] = useState(null)
  const [showScrollTop, setShowScrollTop] = useState(false)
  const heroRef = useRef(null)
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', subject: '', message: '' })
  const [formStatus, setFormStatus] = useState({ state: 'idle', error: '' })

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    const updateMotion = () => setReducedMotion(Boolean(query?.matches))
    updateMotion()
    query?.addEventListener?.('change', updateMotion)
    return () => {
      query?.removeEventListener?.('change', updateMotion)
    }
  }, [])

  useEffect(() => {
    if (reducedMotion || heroPaused || heroInteracting) return undefined
    const timer = setInterval(() => {
      if (!document.hidden) setHeroIndex((index) => (index + 1) % heroImages.length)
    }, 7000)
    return () => clearInterval(timer)
  }, [reducedMotion, heroPaused, heroInteracting])

  useEffect(() => {
    if (!window.IntersectionObserver || !heroRef.current) return undefined
    const observer = new IntersectionObserver(([entry]) => setShowScrollTop(!entry.isIntersecting))
    observer.observe(heroRef.current)
    return () => observer.disconnect()
  }, [])

  const handleChange = (event) => {
    const { name, value } = event.target
    setFormData((previous) => ({ ...previous, [name]: value }))
  }
  const handleSubmit = async (event) => {
    event.preventDefault()
    if (formStatus.state === 'loading') return
    setFormStatus({ state: 'loading', error: '' })
    try {
      const response = await fetch('/api/contact', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData),
      })
      if (!response.ok) throw new Error('Failed to send message')
      setFormStatus({ state: 'sent', error: '' })
      setFormData({ name: '', phone: '', email: '', subject: '', message: '' })
    } catch (error) {
      setFormStatus({ state: 'error', error: 'Unable to send. Please try again.' })
    }
  }
  const openModal = (key) => setActiveModal(key)
  const closeModal = () => setActiveModal(null)
  const formatBrandName = (filename) => {
    const name = filename.replace(/\.[^/.]+$/, '').replace(/(?:_1|_orig|-2)$/, '').replace(/[-_]+/g, ' ')
    return ['ge', 'lg'].includes(name) ? name.toUpperCase() : name.replace(/\b\w/g, (character) => character.toUpperCase())
  }

  return (
    <div className={styles.page} id="top">
      <main id="main-content" className={styles.main}>
        <section className={styles.hero} ref={heroRef} aria-labelledby="home-heading">
          <div className={styles.heroCopy}>
            <p className={styles.heroBrand}>Wholesale USA</p>
            <h1 id="home-heading">The Northeast's Appliance Supplier</h1>
            <p className={styles.subhead}>The Northeast's Largest Full Line Distributor</p>
            <div className={styles.ctaRow}>
              <a className={styles.primaryCta} href="tel:15167996404">Call for a quote <ArrowForwardRoundedIcon aria-hidden="true" /></a>
              <a className={styles.secondaryCta} href="#contact">Email our team</a>
            </div>
          </div>
          <div className={styles.heroVisual} onMouseEnter={() => setHeroInteracting(true)} onMouseLeave={() => setHeroInteracting(false)} onFocus={() => setHeroInteracting(true)} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setHeroInteracting(false) }}>
            <div className={styles.heroMedia}>
              {heroImages.map((image, index) => (
                <img key={image.src} src={image.src} {...getLandingImageProps(image.src, heroImageSizes)} alt={image.alt} width="1663" height="668" loading={index === 0 ? 'eager' : 'lazy'} fetchpriority={index === 0 ? 'high' : 'low'} decoding="async" className={`${styles.heroImage} ${heroIndex === index ? styles.activeSlide : ''}`} aria-hidden={heroIndex !== index} />
              ))}
            </div>
            <div className={styles.heroControls}>
              <div className={styles.slideSelectors}>
                {heroImages.map((image, index) => <button key={image.src} type="button" aria-label={`Show ${image.alt}`} aria-pressed={heroIndex === index} onClick={() => { setHeroIndex(index); setHeroPaused(true) }} className={heroIndex === index ? styles.selectedSlide : ''} />)}
              </div>
            </div>
          </div>
        </section>

        <section className={`${styles.section} ${styles.introduction}`} aria-labelledby="family-heading">
          <div className={styles.familyPanel}>
            <img className={styles.originalLogo} src="/images/wholesale_shield.PNG" {...getLandingImageProps('/images/wholesale_shield.PNG', '(max-width: 767px) 58px, 72px')} alt="Wholesale USA logo" width="1024" height="1024" loading="lazy" decoding="async" />
            <img className={styles.darkLogo} src="/images/wholesale_shield.PNG" {...getLandingImageProps('/images/wholesale_shield.PNG', '72px')} alt="Wholesale USA" width="512" height="512" loading="lazy" decoding="async" />
            <h2 id="family-heading">
              <span className={styles.familyStatement}>Family owned &amp; operated</span>{' '}
              <span className={styles.familySince}>since</span>{' '}
              <span className={styles.familyYear}>1988</span>
            </h2>
            <p>Trusted distribution partner for property managers, contractors, builders, and retail dealers.</p>
            <div className={styles.contactList}>
              <span>Massapequa, NY 11758</span>
              <a href="tel:15167996404">(516) 799-6404</a>
              <a href="mailto:info@wholesaleusany.com">info@wholesaleusany.com</a>
            </div>
          </div>
          <div className={styles.introCopy}>
          <p className={styles.sectionLead}>
          Wholesale USA is a local, family-owned appliance Distributor in New York. We
          sell home appliances, including stoves, refrigerators, freezers, microwaves,
          dishwashers, washers, dryers, and portable air conditioners. With warehouse
          space throughout the Northeast, we have stocked appliances ready for you to
          purchase today. We sell all home appliances, including kitchen appliances
          and laundry appliances. Our friendly, knowledgeable sales professionals will
          help match you with your desired appliance. Our new appliances come with a
          1-year in-home manufacturer warranty, and we provide several extended
          warranty options.
          </p>
          <p className={styles.sectionLead}>
            Wholesale USA offers the best prices on appliances that are hard to find
            elsewhere. With a range of appliances in stock, including black stainless steel
            appliances, there’s something for everyone.
          </p>

          </div>
        </section>

      <section className={`${styles.section} ${styles.about}`} id="about">
        <div className={styles.sectionHeader}>
          <div className={styles.aboutHeading}>
            <p className={styles.sectionLabel}>About us</p>
            <h2>Celebrating {yearsInBusiness} Years in Business!</h2>
          </div>
          <p className={styles.sectionLead}>
            At the heart of Wholesale USA is our sense of family. Since our inception in
            1988 to today, we have made it a priority to foster the kind of business
            relationships that last a lifetime. As a family run business, we know how
            important loyalty is and we are so grateful for our talented team. We believe
            the dedication of our incredible employees and long time customers is the
            key to our shared success over the past {yearsInBusiness} years!
          </p>
          <p className={styles.sectionLead}>
            Since 1988, our company has grown to serve nine states in the Northeast, and
            become the most trusted appliance source for builders, contractors, property
            managers, and retailers who want high quality appliances at competitive
            prices. Our family-owned operation has established a reputation for having
            professionals who are among the most knowledgeable, helpful and honest in
            the industry. Our diverse range of customers have found that when choosing
            among various appliance dealers online and in the area, we are a company
            they can trust.
          </p>
        </div>
      </section>


      <section className={`${styles.section} ${styles.services}`} id="services">
        <div className={styles.sectionHeader}>
          <p className={styles.sectionLabel}>Services</p>
          <h2>What we handle for you</h2>
          <p className={styles.sectionLead}>End-to-end wholesale support so you can focus on your stores.</p>
        </div>

        <div className={styles.serviceRows}>
          <div className={`${styles.serviceRow}`} id="services-property-managers">
            <div className={styles.serviceText}>
              <p className={styles.serviceCategory}>Your Trusted Appliance Dealer for Property Management Companies</p>
              <h3>Reliable Appliance Solutions for Property Managers</h3>
              <p>
                We specialize in providing high-quality appliances to property management
                companies. Whether you oversee multi-unit apartment buildings,
                condominiums, or single-family rental homes, we offer cost-effective, durable,
                and energy-efficient appliances to meet your tenants' needs.
              </p>
              <button className={styles.secondaryCta} type="button" aria-haspopup="dialog" onClick={() => openModal('property')}>
                Learn more <ArrowForwardRoundedIcon aria-hidden="true" />
              </button>
            </div>
            <div className={styles.serviceImage}>
              <img src="/images/PropertyManagement.jpg" {...getLandingImageProps('/images/PropertyManagement.jpg', '(max-width: 767px) calc(100vw - 40px), (max-width: 1416px) calc(50vw - 48px), 660px')} alt="Apartment buildings served by property managers" width="1920" height="1281" loading="lazy" decoding="async" />
            </div>
          </div>

          <div className={`${styles.serviceRow} ${styles.reverse}`} id="services-builders">
            <div className={styles.serviceText}>
              <p className={styles.serviceCategory}>Contractors / Builders</p>
              <h3>Your Trusted Supplier for New Build Appliances</h3>
              <p>
               Wholesale USA specializes in supplying appliances to general contractors,
                builders, and developers for residential and commercial projects. Whether
                you're constructing new homes, renovating apartments, or outfitting multi-
                unit developments, we provide reliable, cost-effective, and high-performance
                appliances tailored to your needs
              </p>
              <button className={styles.secondaryCta} type="button" aria-haspopup="dialog" onClick={() => openModal('builders')}>
                Learn more <ArrowForwardRoundedIcon aria-hidden="true" />
              </button>
            </div>
            <div className={styles.serviceImage}>
              <img src="/images/GeneralContractors.jpg" {...getLandingImageProps('/images/GeneralContractors.jpg', '(max-width: 767px) calc(100vw - 40px), (max-width: 1416px) calc(50vw - 48px), 660px')} alt="Residential building project" width="1920" height="1281" loading="lazy" decoding="async" />
            </div>
          </div>

          <div className={styles.serviceRow} id="services-retail-dealers">
            <div className={styles.serviceText}>
              <p className={styles.serviceCategory}>Retail dealers</p>
              <h3>Your Reliable Supplier for Retail Dealers</h3>
              <p>
                At Wholesale USA, we have built our success side-by-side with Independent
                Retail Appliance and Electronics Dealers by fostering strong relationships.
                Our Retail Dealers Division is committed to forging partnerships with
                Independent Retailers and Local Small Businesses, with an emphasis on
                reliability and integrity.
              </p>
              <button className={styles.secondaryCta} type="button" aria-haspopup="dialog" onClick={() => openModal('retail')}>
                Learn more <ArrowForwardRoundedIcon aria-hidden="true" />
              </button>
            </div>
            <div className={styles.serviceImage}>
              <img src="/images/Landlords.jpg" {...getLandingImageProps('/images/Landlords.jpg', '(max-width: 767px) calc(100vw - 40px), (max-width: 1416px) calc(50vw - 48px), 660px')} alt="Residential properties and appliance supply" width="1920" height="1281" loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </section>


      <ServiceDialog open={activeModal === 'property'} onClose={closeModal} labelId="service-property-title">
              <div className={styles.modalCard}>
                <div className={styles.modalImage}>
                  <img src="/images/PropertyManagement.jpg" {...getLandingImageProps('/images/PropertyManagement.jpg', '(max-width: 868px) calc(100vw - 48px), 820px')} alt="Apartment buildings served by property managers" width="1920" height="1281" loading="lazy" decoding="async" />
                </div>
                <div className={styles.modalBody}>
                  <p className={styles.kicker}>Property management</p>
                  <h2 id="service-property-title">Reliable Appliance Solutions for Property Managers</h2>
                  <p>
                    At Wholesale USA, we specialize in providing high-quality appliances to property management companies.
                    Whether you oversee multi-unit apartment buildings, condominiums, or single-family rental homes,
                    we offer cost-effective, durable, and energy-efficient appliances to meet your tenants&#39; needs.
                  </p>
                  <p>Why partner with us?</p>
                  <ul>
                    <li><strong>Selection &amp; Availability</strong> – With Warehouses throughout the Northeast,
                        we stock all types of appliances from basic, budget-friendly models to
                        top-of-the-line stainless steel luxury appliances.</li>
                    <li><strong>Industry Leading Pricing</strong> – Save more with our exclusive volume
                        discounts and property management pricing plans.</li>
                    <li><strong>Fast &amp; Efficient Fulfillment </strong>– We prioritize quick order processing and
                    timely delivery to minimize downtime for your properties.</li>
                    <li><strong>Turnkey Appliance Packages</strong> – Need to outfit multiple units? We offer
                        tailored appliance packages designed for rental properties.</li>
                    <li>
                        <strong>Five Star Service</strong> – From In-Unit Delivery, to Uncrating, and Old
                        Appliance Removal, we handle everything for a seamless experience.
                    </li>
                  </ul>
                  <h3>Appliances, Air Conditioners, &amp; Electronics</h3>
                  <p>
                    We offer and stock popular product lines that your customers need. Whether
upgrading appliances, or buying a Window AC Unit during a heat wave, we
have you covered.
                  </p>
                  <h3>Serving Property Managers Across the Northeast</h3>
                  <p>
                    No matter where your rental property is located, Wholesale USA provides
                    professional service and top-tier appliances to landlords from Washington
                    D.C. to Boston, MA.
                  </p>
                  <p>
                    Let Wholesale USA be your go-to appliance supplier—because quality,
                      affordability, and convenience matter.
                  </p>
                </div>
              </div>
      </ServiceDialog>
      <ServiceDialog open={activeModal === 'builders'} onClose={closeModal} labelId="service-builders-title">
              <div className={styles.modalCard}>
                <div className={styles.modalImage}>
                  <img src="/images/GeneralContractors.jpg" {...getLandingImageProps('/images/GeneralContractors.jpg', '(max-width: 868px) calc(100vw - 48px), 820px')} alt="Residential building project" width="1920" height="1281" loading="lazy" decoding="async" />
                </div>
                <div className={styles.modalBody}>
                  <p className={styles.kicker}>Contractors / Builders</p>
                  <h2 id="service-builders-title">Your Trusted Supplier for New Build Appliances</h2>
                  <p>
                     At Wholesale USA, we specialize in working with builders of all sizes, from
                      single-family custom homes to four-unit condo conversions to 600-unit
                      multi-family residential buildings. Every builder who partners with us benefits
                      from our expert team, who strive to keep their projects on time and on
                      budget .
                  </p>
                 <p>Why choose Wholesale USA?</p>
                  <ul>
                    <li><strong>Industry Leading Pricing</strong> – Get high-quality appliances at unbeatable
                      prices to maximize your return on investment.</li>
                    <li><strong>Bulk Discounts</strong> – Outfitting multiple rental units? Take advantage of
                      our exclusive bulk pricing options.</li>
                    <li><strong>Fast &amp; Reliable Delivery</strong>– Since time is money, we ensure quick
                      turnaround times, so your sites experience minimal disruption.</li>
                    <li><strong>Wide Selection of Appliances</strong> – From refrigerators and stoves to
                        washers and dryers, we carry top brands to suit every property.</li>
                    <li>
                        <strong>In-Unit Delivery &amp; Uncrating</strong> – Save time with our hassle-free In-Unit
                        Delivery, and we’ll even take away the box and styrofoam.
                    </li>
                    <li>
                        <strong>Durable &amp; Tenant-Friendly Products</strong> – Our appliances are built to
                        withstand rental use, ensuring longevity and easy maintenance.
                    </li>
                  </ul>
                  <h3>Appliance Packages for Matching Units</h3>
                  <p>
                    We offer customized appliance packages based on the size and type of your
                    rental unit. Whether you need a basic appliance set for an economy rental or
                    premium options for high-end properties, we have you covered.
                  </p>
                  <h3>Serving Builders Across the Northeast</h3>
                  <p>
                   No matter where your rental property is located, Wholesale USA provides
                    professional service and top-tier appliances to landlords from Washington
                    D.C. to Boston, MA.
                  </p>
                  <p>
                    Let Wholesale USA be your go-to appliance supplier—because quality,
                      affordability, and convenience matter.
                  </p>
                </div>
              </div>
      </ServiceDialog>
      <ServiceDialog open={activeModal === 'retail'} onClose={closeModal} labelId="service-retail-title">
              <div className={styles.modalCard}>
                <div className={styles.modalImage}>
                  <img src="/images/Landlords.jpg" {...getLandingImageProps('/images/Landlords.jpg', '(max-width: 868px) calc(100vw - 48px), 820px')} alt="Residential properties and appliance supply" width="1920" height="1281" loading="lazy" decoding="async" />
                </div>
                <div className={styles.modalBody}>
                  <p className={styles.kicker}>Retail dealers</p>
                  <h2 id="service-retail-title">Your Reliable Supplier for Retail Dealers</h2>
                  <p>
                    At Wholesale USA, we have built our success side-by-side with Independent
                    Retail Appliance and Electronics Dealers by fostering strong relationships.
                    Our Retail Dealers Division is committed to forging partnerships with
                    Independent Retailers and Local Small Businesses, with an emphasis on
                    reliability and integrity.
                  </p>
                  <p>Why choose Wholesale USA?</p>
                   <ul>
                    <li><strong>Industry Leading Pricing</strong> – Get high-quality appliances at unbeatable
                        prices to maximize your profits.</li>
                    <li><strong>Fast &amp; Reliable Delivery</strong> – As soon as Next Day Delivery, your customers
                      won’t have to wait or shop around.</li>
                    <li><strong>Popular Appliances In Stock</strong>– We have our most popular models in
                        inventory so ensure quick turnaround.</li>
                    <li><strong>Consumer Delivery</strong> – Save time and let us deliver to your customer in
                      select markets.</li>
                  </ul>
                  <h3>Appliances, Air Conditioners, &amp; Electronics</h3>
                  <p>
                    We offer and stock popular product lines that your customers need. Whether
                    upgrading appliances, or buying a Window AC Unit during a heat wave, we
                    have you covered.
                  </p>
                  <h3>Serving Retail Dealers Across the Northeast</h3>
                  <p>
                   No matter where your rental property is located, Wholesale USA provides
                    professional service and top-tier appliances to landlords from Washington
                    D.C. to Boston, MA.
                  </p>
                  <p>
                    Let Wholesale USA be your go-to appliance supplier—because quality,
                      affordability, and convenience matter.
                  </p>
                </div>
              </div>
      </ServiceDialog>

      <section className={`${styles.section} ${styles.brands}`} id="brands">
        <div className={styles.sectionHeader}>
          <p className={styles.sectionLabel}>Brands</p>
          <h2>Brands We Carry</h2>
          <p className={styles.sectionLead}>Call or e-mail for inquiries about any brand you do not see.</p>
        </div>
        <div className={styles.brandGrid}>
          {brandLogos.map((logo) => <div key={logo} className={styles.brandCard}><img src={`/images/brand_logos/${logo}`} {...getLandingImageProps(`/images/brand_logos/${logo}`, '112px')} alt={`${formatBrandName(logo)} logo`} className={styles.brandLogo} width="800" height="800" loading="lazy" decoding="async" /></div>)}
        </div>
        <div className={styles.brandCarousel} aria-label="Appliance brands">
          <button className={styles.brandCarouselControl} type="button" aria-label="Previous brand" onClick={() => setBrandIndex((index) => (index - 1 + brandLogos.length) % brandLogos.length)}>‹</button>
          <div className={styles.brandCarouselFrame}>
            {[(brandIndex - 1 + brandLogos.length) % brandLogos.length, brandIndex, (brandIndex + 1) % brandLogos.length].map((index, position) => <img key={`${index}-${position}`} src={`/images/brand_logos/${brandLogos[index]}`} {...getLandingImageProps(`/images/brand_logos/${brandLogos[index]}`, position === 1 ? '140px' : '92px')} alt={`${formatBrandName(brandLogos[index])} logo`} className={position === 1 ? styles.brandActive : styles.brandAdjacent} width="800" height="800" loading="lazy" decoding="async" />)}
          </div>
          <button className={styles.brandCarouselControl} type="button" aria-label="Next brand" onClick={() => setBrandIndex((index) => (index + 1) % brandLogos.length)}>›</button>
        </div>
      </section>

      <section className={`${styles.section} ${styles.contact}`} id="contact">
        <div className={styles.sectionHeader}>
          <h2>Contact us</h2>
          <p className={styles.sectionLead}>Call or email and we’ll share availability, pricing, and routes for your stores.</p>
        </div>

        <div className={styles.contactCardCombined}>
          <div className={styles.contactIntro}>
            <h3>Contact Wholesale USA</h3>
            <div className={styles.contactList}>
              <a href="tel:15167996404">Phone: (516) 799-6404</a>
              <a href="mailto:info@wholesaleusany.com">Email: info@wholesaleusany.com</a>
              <span>Massapequa, NY 11758</span>
            </div>
            <div className={styles.ctaRow}>
              <a className={styles.primaryCta} href="tel:15167996404">Call now</a>
              <a className={styles.secondaryCta} href="mailto:info@wholesaleusany.com">Email us</a>
            </div>
          </div>

          <div className={styles.contactFormWrapper}>
            <h3>Send us a message</h3>
            <form className={styles.contactForm} onSubmit={handleSubmit} aria-busy={formStatus.state === 'loading'}>
              <div className={styles.formRow}>
                <label>
                  Name
                  <input autoComplete="name" name="name" value={formData.name} onChange={handleChange} required />
                </label>
                <label>
                  Phone
                  <input type="tel" autoComplete="tel" name="phone" value={formData.phone} onChange={handleChange} />
                </label>
              </div>
              <div className={styles.formRow}>
                <label>
                  Email
                  <input type="email" autoComplete="email" name="email" value={formData.email} onChange={handleChange} required />
                </label>
                <label>
                  Subject
                  <input name="subject" value={formData.subject} onChange={handleChange} required />
                </label>
              </div>
              <label className={styles.fullRow}>
                Message
                <textarea name="message" rows="5" value={formData.message} onChange={handleChange} required />
              </label>
              <div className={styles.formActions}>
                <button type="submit" className={styles.primaryCta} disabled={formStatus.state === 'loading'}>
                  {formStatus.state === 'loading' ? 'Sending...' : 'Send message'}
                </button>
                {formStatus.state === 'sent' && <span role="status" className={styles.formSuccess}>Message sent!</span>}
                {formStatus.state === 'error' && <span role="alert" className={styles.formError}>{formStatus.error}</span>}
              </div>
            </form>
          </div>
        </div>
      </section>


      </main>
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerBrand}>
            <img className={styles.originalLogo} src="/images/wholesale_shield.PNG" {...getLandingImageProps('/images/wholesale_shield.PNG', '(max-width: 767px) 50px, 58px')} alt="Wholesale USA shield" width="1024" height="1024" loading="lazy" decoding="async" />
            <img className={styles.darkLogo} src="/images/wholesale_shield.PNG" {...getLandingImageProps('/images/wholesale_shield.PNG', '72px')} alt="Wholesale USA" width="512" height="512" loading="lazy" decoding="async" />
            <div>
              <p className={styles.footerName}>Wholesale U.S.A.</p>
              <p className={styles.footerTag}>The Northeast's Largest Full Line Disributor</p>
            </div>
          </div>
          <div className={styles.footerColumn}>
            <h2>Explore</h2>
            <a href="#about">About</a>
            <a href="#services">Services</a>
            <a href="#contact">Contact</a>
          </div>
          <div className={styles.footerColumn}>
            <h2>Contact</h2>
            <a href="tel:15167996404">(516) 799-6404</a>
            <a href="mailto:info@wholesaleusany.com">info@wholesaleusany.com</a>
            <span>Massapequa, NY 11758</span>
          </div>
        </div>
        <div className={styles.footerBottom}>
          <span>Wholesale USA © 2022</span>
          <a href="#top">Back to top</a>
        </div>
      </footer>

      {showScrollTop && <a className={styles.scrollTop} href="#top" aria-label="Scroll to top">↑</a>}
    </div>
  )
}

export default Home
